import { useEffect, useMemo, useState, useRef } from 'react'
import { Clock, Minus, AlertTriangle, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { DayOfWeek, getDayOfWeek } from '@meiuca/web-foundation'
import { Button, ButtonVariant, Body } from '@meiuca/ui-components'
import { useCourtAvailability } from '@/hooks/useCourtAvailability'
import { remoteConfigService } from '@/lib/firebase'
import { toastManager } from '@/lib/toastManager'
import type { SelectedSlotDetail } from '../types'

interface CourtAvailabilitySelectorProps {
  venueId: string
  selectedModality: string | null
  selectedSlotDetails: SelectedSlotDetail[]
  onSlotsChange: (slots: SelectedSlotDetail[]) => void
}

interface AvailableCourt {
  id: string
  name: string
  /** Preço de tabela — é o que a criação envia; campanha nunca altera. */
  pricePerHour?: number
  /** Preço final com campanha; ausente sem campanha (exibição apenas). */
  campaignPricePerHour?: number
  color?: string
}

interface AvailableTimeSlot {
  time: string
  date: string
  availableCourts: AvailableCourt[]
  averagePrice?: number
}

const DAYS_OF_WEEK: { id: DayOfWeek; label: string; short: string }[] = [
  { id: DayOfWeek.SUNDAY, label: 'Domingo', short: 'DOM' },
  { id: DayOfWeek.MONDAY, label: 'Segunda', short: 'SEG' },
  { id: DayOfWeek.TUESDAY, label: 'Terça', short: 'TER' },
  { id: DayOfWeek.WEDNESDAY, label: 'Quarta', short: 'QUA' },
  { id: DayOfWeek.THURSDAY, label: 'Quinta', short: 'QUI' },
  { id: DayOfWeek.FRIDAY, label: 'Sexta', short: 'SEX' },
  { id: DayOfWeek.SATURDAY, label: 'Sábado', short: 'SAB' }
]

const SLOT_DURATION_MINUTES = 60

const startOfWeek = (date: Date) => {
  const d = new Date(date)
  const day = d.getDay() // 0-6
  const diff = d.getDate() - day // Sunday as start
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d
}

const addDays = (date: Date, days: number) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const minutesToTimeString = (minutes: number) => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// Keeps only the longest contiguous run of 1h slots (earliest run wins ties)
const longestContiguousRun = (slots: SelectedSlotDetail[]) => {
  const sorted = [...slots].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time))
  let best: SelectedSlotDetail[] = []
  let run: SelectedSlotDetail[] = []
  for (const slot of sorted) {
    const previous = run[run.length - 1]
    run =
      previous && timeToMinutes(slot.time) - timeToMinutes(previous.time) === SLOT_DURATION_MINUTES
        ? [...run, slot]
        : [slot]
    if (run.length > best.length) best = run
  }
  return best
}

const formatPrice = (priceInCents?: number) => {
  if (priceInCents === undefined) return 'R$ 0,00'
  const reais = Math.floor(priceInCents / 100)
  const centavos = priceInCents % 100
  return `R$ ${reais},${centavos.toString().padStart(2, '0')}`
}

export function CourtAvailabilitySelector({
  venueId,
  selectedModality,
  selectedSlotDetails,
  onSlotsChange
}: CourtAvailabilitySelectorProps) {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(startOfWeek(new Date()))
  const { availability, loading, error, refetch } = useCourtAvailability({
    venueId,
    currentWeekStart,
    modality: selectedModality || undefined,
    enabled: !!venueId
  })

  const maxDaysInAdvance = remoteConfigService.getNumber('bookingMaxDaysInAdvance') ?? 15

  // Calcula a data máxima permitida para reservas
  const maxAllowedDate = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const maxDate = new Date(today)
    maxDate.setDate(maxDate.getDate() + maxDaysInAdvance)
    return maxDate
  }, [maxDaysInAdvance])

  const availabilityByDay = useMemo(() => {
    if (!availability) return new Map<DayOfWeek, { date: string; slots: AvailableTimeSlot[] }>()

    const courtsMap = new Map(availability.courts.map(court => [court.id, court]))
    const map = new Map<DayOfWeek, { date: string; slots: AvailableTimeSlot[] }>()
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    availability.availableSchedules.forEach(schedule => {
      const dateOnly = schedule.date.split('T')[0]
      const [year, month, day] = dateOnly.split('-').map(Number)
      const localDate = new Date(year, month - 1, day)

      if (localDate < today || localDate > maxAllowedDate) {
        return
      }

      const dayOfWeek = getDayOfWeek(localDate)

      const slots = (schedule.availables ?? (schedule as any).availableSlots ?? []).map(slot => {
        const start = slot.startMinutes ?? (slot as any).startsAt ?? 0
        const courtPricings = slot.courtPricings ?? []

        const availableCourts: AvailableCourt[] = courtPricings.map((pricing: any) => {
          const court = courtsMap.get(pricing.courtId)
          return {
            id: pricing.courtId,
            name: court?.name ?? `Quadra ${pricing.courtId}`,
            pricePerHour: pricing.priceCents ?? court?.pricePerHour,
            campaignPricePerHour: pricing.campaignPriceCents ?? undefined,
            color: (court as any)?.color
          }
        })

        // Média do valor EFETIVO (com campanha quando houver) — "somente o
        // valor", sem riscado na listagem.
        const averagePrice = availableCourts.length
          ? Math.round(
              availableCourts.reduce(
                (sum, court) => sum + (court.campaignPricePerHour ?? court.pricePerHour ?? 0),
                0
              ) / availableCourts.length
            )
          : undefined

        return {
          time: minutesToTimeString(start),
          date: dateOnly,
          availableCourts,
          averagePrice
        }
      }).sort((a, b) => a.time.localeCompare(b.time))

      map.set(dayOfWeek, { date: dateOnly, slots })
    })

    return map
  }, [availability, maxAllowedDate])

  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(getDayOfWeek(new Date()))
  const dayRefs = useRef<Map<DayOfWeek, HTMLButtonElement>>(new Map())
  const hasScrolled = useRef(false)

  useEffect(() => {
    if (!availabilityByDay.size) return
    const availableDays = Array.from(availabilityByDay.keys())
    if (!availableDays.includes(selectedDay)) {
      setSelectedDay(availableDays[0])
    }
  }, [availabilityByDay, selectedDay])

  // Scroll automático para o dia selecionado no primeiro carregamento
  useEffect(() => {
    if (!hasScrolled.current && availabilityByDay.size > 0 && dayRefs.current.has(selectedDay)) {
      const selectedButton = dayRefs.current.get(selectedDay)
      if (selectedButton) {
        selectedButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        hasScrolled.current = true
      }
    }
  }, [availabilityByDay, selectedDay])

  // Prune selections that are no longer available. A slot can only be checked when its
  // exact date is present in the loaded availability (checking against currentWeekStart
  // would race the fetch while navigating weeks); dates not loaded are left untouched.
  // If pruning opens a gap, keep the longest contiguous run.
  useEffect(() => {
    if (availabilityByDay.size === 0) return

    const dayDataByDate = new Map(
      Array.from(availabilityByDay.values()).map(dayData => [dayData.date, dayData])
    )

    const isStale = (slot: SelectedSlotDetail) => {
      const dayData = dayDataByDate.get(slot.date)
      return !!dayData && !dayData.slots.some(s => s.time === slot.time)
    }

    if (selectedSlotDetails.some(isStale)) {
      onSlotsChange(longestContiguousRun(selectedSlotDetails.filter(slot => !isStale(slot))))
    }
  }, [availabilityByDay, onSlotsChange, selectedSlotDetails])

  const slotsForDay = availabilityByDay.get(selectedDay)?.slots ?? []

  // Selection invariant: a single date + a contiguous block of 1h slots
  const selectionDate = selectedSlotDetails[0]?.date ?? null

  const blockRange = useMemo(() => {
    if (selectedSlotDetails.length === 0) return null
    const minutes = selectedSlotDetails.map(s => timeToMinutes(s.time)).sort((a, b) => a - b)
    return { start: minutes[0], end: minutes[minutes.length - 1] }
  }, [selectedSlotDetails])

  const isBlockEdge = (time: string) => {
    if (blockRange === null) return false
    const minutes = timeToMinutes(time)
    return minutes === blockRange.start || minutes === blockRange.end
  }

  const isAdjacentToBlock = (slot: AvailableTimeSlot) => {
    if (blockRange === null || selectionDate !== slot.date) return false
    const minutes = timeToMinutes(slot.time)
    return (
      minutes === blockRange.start - SLOT_DURATION_MINUTES ||
      minutes === blockRange.end + SLOT_DURATION_MINUTES
    )
  }

  const getSelectedCourts = (date: string, time: string) => {
    return selectedSlotDetails.find(slot => slot.date === date && slot.time === time)?.courts ?? []
  }

  const totalSelected = useMemo(() => {
    const totalSlots = selectedSlotDetails.length
    const totalCourts = selectedSlotDetails.reduce((sum, slot) => sum + slot.courts.length, 0)
    // Valor EFETIVO (com campanha) — o que o cliente vai pagar.
    const totalPrice = selectedSlotDetails.reduce((sum, slot) => {
      return (
        sum +
        slot.courts.reduce(
          (courtSum, court) => courtSum + (court.campaignPricePerHour ?? court.pricePerHour ?? 0),
          0
        )
      )
    }, 0)
    return { totalSlots, totalCourts, totalPrice }
  }, [selectedSlotDetails])

  // Single tap on a court chip: selects the slot with that court in one go.
  // Adjacent slots extend the block; distant slots (or another date) start a new one.
  // Within a selected slot it toggles the court; removing the last court removes the
  // slot, which is only allowed at the block edges (no gaps).
  const selectCourt = (time: string, courtId: string) => {
    const dayData = availabilityByDay.get(selectedDay)
    const slot = dayData?.slots.find(s => s.time === time)
    if (!slot) return

    const targetCourt = slot.availableCourts.find(c => c.id === courtId)
    if (!targetCourt) return

    const current = selectedSlotDetails.find(s => s.date === slot.date && s.time === slot.time)

    if (!current) {
      const newSlot: SelectedSlotDetail = {
        day: selectedDay,
        date: slot.date,
        time: slot.time,
        courts: [targetCourt]
      }
      const extendsBlock = selectedSlotDetails.length === 0 || isAdjacentToBlock(slot)
      if (!extendsBlock) {
        toastManager.warning('Os horários precisam ser em sequência no mesmo dia — iniciamos uma nova seleção.')
      }
      onSlotsChange(extendsBlock ? [...selectedSlotDetails, newSlot] : [newSlot])
      return
    }

    const isCourtSelected = current.courts.some(c => c.id === courtId)

    if (!isCourtSelected) {
      onSlotsChange(
        selectedSlotDetails.map(s =>
          s.date === slot.date && s.time === slot.time
            ? { ...s, courts: [...s.courts, targetCourt] }
            : s
        )
      )
      return
    }

    if (current.courts.length > 1) {
      onSlotsChange(
        selectedSlotDetails.map(s =>
          s.date === slot.date && s.time === slot.time
            ? { ...s, courts: s.courts.filter(c => c.id !== courtId) }
            : s
        )
      )
      return
    }

    if (isBlockEdge(slot.time)) {
      removeSlot(slot.date, slot.time)
    }
  }

  const removeSlot = (date: string, time: string) => {
    onSlotsChange(selectedSlotDetails.filter(slot => !(slot.date === date && slot.time === time)))
  }

  const goToWeek = (direction: 'prev' | 'next') => {
    setCurrentWeekStart(prev => addDays(prev, direction === 'next' ? 7 : -7))
  }

  const formatWeekRange = () => {
    const endOfWeek = addDays(currentWeekStart, 6)
    return `${currentWeekStart.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - ${endOfWeek.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-border-light border-t-brand-primary rounded-full animate-spin mb-4" />
        <Body className="text-text-secondary">Carregando disponibilidade...</Body>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 border border-feedback-error/30 bg-feedback-error/5 rounded-lg text-center">
        <div className="flex items-center justify-center gap-2 text-feedback-error font-medium mb-2">
          <AlertTriangle className="w-5 h-5" />
          Erro ao carregar disponibilidade
        </div>
        <Body className="text-text-secondary mb-4">{error}</Body>
        <Button variant={ButtonVariant.PRIMARY} onClick={refetch}>Tentar novamente</Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between bg-background-secondary rounded-lg p-2">
          <div className="flex items-center gap-1.5 text-text-secondary">
            <CalendarDays className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{formatWeekRange()}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => goToWeek('prev')}
              className="p-1.5 hover:bg-background-primary rounded transition"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => goToWeek('next')}
              className="p-1.5 hover:bg-background-primary rounded transition"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {DAYS_OF_WEEK.map(day => {
            const dayData = availabilityByDay.get(day.id)
            const isSelected = selectedDay === day.id
            return (
              <button
                key={day.id}
                ref={(el) => {
                  if (el) {
                    dayRefs.current.set(day.id, el)
                  } else {
                    dayRefs.current.delete(day.id)
                  }
                }}
                onClick={() => dayData && setSelectedDay(day.id)}
                className={`flex-1 p-2 rounded border text-left transition min-w-[100px] ${
                  isSelected
                    ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                    : dayData
                    ? 'border-border-light hover:border-brand-primary/40'
                    : 'border-border-light text-text-disabled bg-background-secondary cursor-not-allowed'
                }`}
                disabled={!dayData}
              >
                <div className="text-[11px] uppercase tracking-wide font-medium">{day.short}</div>
                <div className="text-sm font-semibold">{dayData ? dayData.date.split('-').slice(1).join('/') : '--'}</div>
                <div className="text-[11px] text-text-secondary">
                  {dayData ? `${dayData.slots.length}h` : '--'}
                </div>
              </button>
            )
          })}
        </div>

        <div className="space-y-2">
          {slotsForDay.length === 0 && (
            <div className="text-center py-8 border border-border-light rounded-lg">
              <Body className="text-text-secondary text-sm">
                Nenhum horário disponível para este dia.
              </Body>
            </div>
          )}

          {slotsForDay.map(slot => {
            const isSelected = selectedSlotDetails.some(
              s => s.date === slot.date && s.time === slot.time
            )
            // Distant slots stay tappable, but tapping one starts a new block
            const isDistant = blockRange !== null && !isSelected && !isAdjacentToBlock(slot)
            const isRemovable = isSelected && isBlockEdge(slot.time)
            const selectedCourts = getSelectedCourts(slot.date, slot.time)

            return (
              <div
                key={slot.time}
                title={isDistant ? 'Selecionar aqui inicia uma nova seleção' : undefined}
                className={`relative p-1.5 border rounded-lg transition-all duration-200 text-xs shadow-sm ${
                  isSelected
                    ? 'border-brand-primary bg-brand-primary/5'
                    : isDistant
                      ? 'border-border-light opacity-50 hover:opacity-80'
                      : 'border-border-light hover:border-brand-primary/20 hover:shadow-md'
                }`}
              >
                {/* Indicator line */}
                <div
                  className="absolute top-0 left-0 w-1 h-full rounded-l-lg"
                  style={{ backgroundColor: isSelected ? 'var(--brand-primary)' : '#e5e7eb' }}
                />

                {/* Line 1: time + remove button (block edges only) */}
                <div className="ml-2 flex items-center justify-between min-w-0 min-h-[28px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0 text-text-secondary" />
                    <span className="font-semibold text-sm leading-tight">{slot.time}</span>
                  </div>

                  {isRemovable && (
                    <button
                      onClick={() => removeSlot(slot.date, slot.time)}
                      aria-label={`Remover ${slot.time}`}
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition bg-red-50 hover:bg-red-100"
                    >
                      <Minus className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  )}
                </div>

                {/* Line 2: courts always visible — one tap selects slot + court */}
                <div className="ml-2 mt-1.5 flex flex-wrap gap-2">
                  {slot.availableCourts.map(court => {
                    const isCourtSelected = isSelected && selectedCourts.some(c => c.id === court.id)
                    return (
                      <button
                        key={court.id}
                        onClick={() => selectCourt(slot.time, court.id)}
                        className={`inline-flex items-center min-h-[44px] px-3.5 py-2.5 rounded-lg text-xs font-semibold border transition leading-tight ${
                          isCourtSelected
                            ? 'bg-brand-primary text-white border-brand-primary'
                            : 'bg-white text-text-primary border-border-light hover:border-brand-primary/40 active:bg-brand-primary/5'
                        }`}
                      >
                        {court.name} ·{' '}
                        {court.campaignPricePerHour != null ? (
                          // Preço promocional lê "de → por" (riscado + destaque),
                          // sem selo e sem nome de campanha.
                          <>
                            <span className="line-through opacity-60">
                              {formatPrice(court.pricePerHour ?? slot.averagePrice)}
                            </span>{' '}
                            <span className="font-semibold text-feedback-success">
                              {formatPrice(court.campaignPricePerHour)}
                            </span>
                          </>
                        ) : (
                          formatPrice(court.pricePerHour ?? slot.averagePrice)
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Resumo fixo - Full screen acima do footer */}
      {totalSelected.totalSlots > 0 && (
        <div className="fixed bottom-[72px] left-0 right-0 z-30 bg-brand-primary border-t border-brand-primary/20 shadow-lg">
          <div className="max-w-5xl mx-auto px-6 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-xs text-white/90">
                  {totalSelected.totalSlots} horário(s) selecionado(s)
                </div>
                <button
                  onClick={() => onSlotsChange([])}
                  className="text-xs text-white/80 underline hover:text-white transition"
                >
                  Limpar
                </button>
              </div>
              <div className="text-sm font-semibold text-white">
                {formatPrice(totalSelected.totalPrice)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
